import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFirmTaxSettings, updateFirmTaxSettings, fetchGLConfig, updateGLConfig, listGLAccounts } from '../api';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const FREQUENCY_OPTIONS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'BI_MONTHLY', label: 'Bi-Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
];

function FirmTaxSettings({ authAxios, showFormMessage, userRole }) {
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // GL Config state
  const [glConfig, setGlConfig] = useState(null);
  const [glFormData, setGlFormData] = useState({});
  const [glAccounts, setGlAccounts] = useState([]);
  const [glDirty, setGlDirty] = useState(false);
  const [glSaving, setGlSaving] = useState(false);

  const canEdit = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getFirmTaxSettings(authAxios);
      setSettings(data);
      setFormData(data);
      setDirty(false);
    } catch {
      showFormMessage('error', 'Failed to load firm tax settings.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Load GL config and accounts
  useEffect(() => {
    const loadGL = async () => {
      try {
        const [cfg, accts] = await Promise.all([
          fetchGLConfig(authAxios),
          listGLAccounts(authAxios, { is_active: true, page_size: 500 }),
        ]);
        setGlConfig(cfg);
        setGlFormData(cfg);
        setGlAccounts(Array.isArray(accts) ? accts : []);
      } catch {
        // GL config may not exist for this firm yet
      }
    };
    loadGL();
  }, [authAxios]);

  const updateField = (field, value) => {
    setFormData(f => ({ ...f, [field]: value }));
    setDirty(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const data = await updateFirmTaxSettings(authAxios, formData);
      setSettings(data);
      setFormData(data);
      setDirty(false);
      showFormMessage('success', 'Firm tax settings updated.');
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to save settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(settings);
    setDirty(false);
  };

  const updateGlField = (field, value) => {
    setGlFormData(f => ({ ...f, [field]: value }));
    setGlDirty(true);
  };

  const handleGlSave = async (e) => {
    e.preventDefault();
    setGlSaving(true);
    try {
      const data = await updateGLConfig(authAxios, glFormData);
      setGlConfig(data);
      setGlFormData(data);
      setGlDirty(false);
      showFormMessage('success', 'GL account mappings updated.');
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to save GL mappings.');
    } finally {
      setGlSaving(false);
    }
  };

  const handleGlReset = () => {
    setGlFormData(glConfig);
    setGlDirty(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="animate__animated animate__fadeIn max-w-2xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Firm Tax Settings</h2>
        <p className="text-gray-500 text-sm mt-1">Manage VAT registration, tax rates, financial year and filing frequency</p>
      </div>

      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm p-6 space-y-6">
        {/* Tax Enabled */}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox" checked={formData.is_taxable || false}
              onChange={e => updateField('is_taxable', e.target.checked)} disabled={!canEdit}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Firm is taxable (apply VAT to invoices)
          </label>
        </div>

        {/* Tax Rate */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
            <input type="number" step="0.01" min="0" max="100"
              value={formData.tax_rate ?? ''} onChange={e => updateField('tax_rate', e.target.value)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VAT Registration Number</label>
            <input type="text" placeholder="e.g. P00123456789"
              value={formData.vat_registration_number || ''} onChange={e => updateField('vat_registration_number', e.target.value)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PAYE / ITW8 Reference</label>
            <input type="text" placeholder="BURS employer PAYE reference"
              value={formData.paye_reference || ''} onChange={e => updateField('paye_reference', e.target.value)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bill of Costs VAT Rate (%)</label>
            <input type="number" step="0.01" min="0" max="100"
              value={formData.boc_vat_rate ?? ''} onChange={e => updateField('boc_vat_rate', e.target.value)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
            <p className="text-xs text-gray-400 mt-1">Separate rate for Bill of Costs (default 12%)</p>
          </div>
        </div>

        {/* Financial Year & Filing */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year Start Month</label>
            <select value={formData.financial_year_start_month ?? 7}
              onChange={e => updateField('financial_year_start_month', parseInt(e.target.value))}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              {MONTH_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VAT Filing Frequency</label>
            <select value={formData.vat_filing_frequency || 'BI_MONTHLY'}
              onChange={e => updateField('vat_filing_frequency', e.target.value)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              {FREQUENCY_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
          <strong>Botswana defaults:</strong> 14% VAT, financial year starts July, bi-monthly filing with BURS. Adjust as needed for your firm.
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={handleReset} disabled={!dirty}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-50">
              Reset
            </button>
            <button type="submit" disabled={!dirty || isSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50">
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </form>

      {/* GL Account Mappings */}
      {glConfig && (
        <form onSubmit={handleGlSave} className="bg-white rounded-xl shadow-sm p-6 space-y-6 mt-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">GL Account Mappings</h3>
            <p className="text-gray-500 text-xs mt-1">Map default GL accounts used by automated journal entries</p>
          </div>

          {/* Core accounts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { field: 'bank_account_code', label: 'Bank Account' },
              { field: 'ar_account_code', label: 'Accounts Receivable' },
              { field: 'ap_account_code', label: 'Accounts Payable' },
              { field: 'revenue_account_code', label: 'Revenue' },
              { field: 'vat_output_account_code', label: 'VAT Output' },
              { field: 'vat_input_account_code', label: 'VAT Input' },
              { field: 'equity_account_code', label: 'Equity / Opening Balance' },
              { field: 'retained_earnings_code', label: 'Retained Earnings' },
              { field: 'bad_debt_expense_code', label: 'Bad Debt Expense' },
              { field: 'trust_bank_account_code', label: 'Trust Bank Account' },
              { field: 'trust_liability_account_code', label: 'Trust Liability' },
              { field: 'petty_cash_account_code', label: 'Petty Cash' },
              { field: 'debt_recovery_revenue_code', label: 'Debt Recovery Revenue' },
            ].map(({ field, label }) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <select value={glFormData[field] || ''} onChange={e => updateGlField(field, e.target.value)}
                  disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
                  <option value="">— Select account —</option>
                  {glAccounts.map(a => <option key={a.id} value={a.code}>{a.code} – {a.name}</option>)}
                </select>
              </div>
            ))}
          </div>

          {/* Payroll accounts */}
          <div>
            <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">Payroll Accounts</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { field: 'salary_expense_code', label: 'Salary Expense' },
                { field: 'benefits_expense_code', label: 'Benefits Expense' },
                { field: 'paye_payable_code', label: 'PAYE Payable' },
                { field: 'pension_payable_code', label: 'Pension Payable' },
                { field: 'medical_aid_payable_code', label: 'Medical Aid Payable' },
              ].map(({ field, label }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <select value={glFormData[field] || ''} onChange={e => updateGlField(field, e.target.value)}
                    disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
                    <option value="">— Select account —</option>
                    {glAccounts.map(a => <option key={a.id} value={a.code}>{a.code} – {a.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Disposal / Cost Recovery */}
          <div>
            <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">Other Accounts</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { field: 'cost_recovery_revenue_code', label: 'Cost Recovery Revenue' },
                { field: 'disposal_gain_code', label: 'Disposal Gain' },
                { field: 'disposal_loss_code', label: 'Disposal Loss' },
              ].map(({ field, label }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <select value={glFormData[field] || ''} onChange={e => updateGlField(field, e.target.value)}
                    disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
                    <option value="">— Select account —</option>
                    {glAccounts.map(a => <option key={a.id} value={a.code}>{a.code} – {a.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* GL Auto-Flag Threshold */}
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Flag Threshold (BWP)</label>
            <input type="number" step="0.01" min="0"
              value={glFormData.gl_auto_flag_threshold ?? ''} onChange={e => updateGlField('gl_auto_flag_threshold', e.target.value)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
              placeholder="Entries above this are auto-flagged" />
          </div>

          {canEdit && (
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleGlReset} disabled={!glDirty}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-50">
                Reset
              </button>
              <button type="submit" disabled={!glDirty || glSaving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50">
                {glSaving ? 'Saving...' : 'Save GL Mappings'}
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
}

export default FirmTaxSettings;
